import "./styles/index.css";
import "./styles/gallery.css";

import { FC, Fragment, useEffect, useRef, useState, useCallback } from "react";
import type { ListingGalleryImage } from "./utils/types";
import { useLastViewedPhoto } from "./utils/useLastViewedPhoto";
import { Dialog, Transition } from "@headlessui/react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

export const DEMO_IMAGE: ListingGalleryImage[] = [];

export const getNewParam = ({
  paramName = "photoId",
  value,
}: {
  paramName?: string;
  value: string | number;
}) => {
  let params = new URLSearchParams(document.location.search);
  params.set(paramName, String(value));
  return params.toString();
};

interface Props {
  images?: ListingGalleryImage[];
  onClose?: () => void;
  isShowModal: boolean;
}

const ListingImageGallery: FC<Props> = ({
  images = [],
  onClose,
  isShowModal,
}) => {
  let [searchParams] = useSearchParams();
  const photoId = searchParams?.get("photoId");
  const navigate = useNavigate();
  const location = useLocation();
  const [lastViewedPhoto, setLastViewedPhoto] = useLastViewedPhoto();
  const lastViewedPhotoRef = useRef<HTMLDivElement>(null);

  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const [zoomed, setZoomed] = useState(false);
  const [transitioning, setTransitioning] = useState(false);
  const railRef = useRef<HTMLDivElement>(null);
  const stripRef = useRef<HTMLDivElement>(null);

  // Sync photoId → lightbox
  useEffect(() => {
    if (photoId !== null) {
      const idx = images.findIndex((img) => String(img.id) === photoId);
      setActiveIdx(idx >= 0 ? idx : 0);
      setLightboxOpen(true);
    } else {
      setLightboxOpen(false);
    }
  }, [photoId, images]);

  // Scroll back to last viewed image
  useEffect(() => {
    if (lastViewedPhoto && !photoId) {
      lastViewedPhotoRef.current?.scrollIntoView({ block: "center" });
      setLastViewedPhoto(null);
    }
  }, [photoId, lastViewedPhoto, setLastViewedPhoto]);

  // Scroll rail/strip thumb into view
  useEffect(() => {
    const railThumb = railRef.current?.children[activeIdx] as HTMLElement;
    railThumb?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    const stripThumb = stripRef.current?.children[activeIdx] as HTMLElement;
    stripThumb?.scrollIntoView({ behavior: "smooth", inline: "nearest" });
  }, [activeIdx]);

  const closeLightbox = useCallback(() => {
    // @ts-ignore
    setLastViewedPhoto(photoId);
    let params = new URLSearchParams(document.location.search);
    params.delete("photoId");
    navigate(`?${params.toString()}`, {
      relative: "path",
      state: location.state,
    });
    setZoomed(false);
  }, [photoId, navigate, location.state]);

  const closeGallery = useCallback(() => {
    setZoomed(false);
    setLightboxOpen(false);
    onClose?.();
  }, [onClose]);

  const openLightbox = (id: number) => {
    const newPathname = getNewParam({ value: id });
    navigate(`?${newPathname}`, {
      relative: "path",
      state: location.state,
    });
  };

  const goTo = useCallback((idx: number) => {
    if (idx < 0 || idx >= images.length || transitioning) return;
    setTransitioning(true);
    setZoomed(false);
    setTimeout(() => {
      const target = images[idx];
      const newPathname = getNewParam({ value: target.id });
      navigate(`?${newPathname}`, { relative: "path", state: location.state });
      setActiveIdx(idx);
      setTransitioning(false);
    }, 180);
  }, [images, transitioning, navigate, location.state]);

  // Keyboard navigation
  useEffect(() => {
    if (!lightboxOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") goTo(activeIdx + 1);
      if (e.key === "ArrowLeft")  goTo(activeIdx - 1);
      if (e.key === "Escape")     closeGallery();
      if (e.key === "z")          setZoomed((z) => !z);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [lightboxOpen, activeIdx, goTo, closeGallery]);

  // ── LIGHTBOX ──────────────────────────────────────────
  const renderLightbox = () => {
    if (!lightboxOpen || images.length === 0) return null;
    const current = images[activeIdx];
    return (
      <div className="mg-lightbox">
        {/* Top bar — always visible above image */}
        <div className="mg-lightbox__bar">
          <button
            type="button"
            className="mg-close-btn"
            onClick={closeGallery}
            aria-label="Close gallery"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
            Close
          </button>

          <span className="mg-lightbox__count">
            {activeIdx + 1} / {images.length}
          </span>

          <div className="mg-lightbox__actions">
            <button
              type="button"
              className="mg-icon-btn"
              onClick={() => setZoomed((z) => !z)}
              title="Toggle zoom (Z)"
              aria-label="Toggle zoom"
            >
              {zoomed ? (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /><line x1="8" y1="11" x2="14" y2="11" />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                  <line x1="11" y1="8" x2="11" y2="14" /><line x1="8" y1="11" x2="14" y2="11" />
                </svg>
              )}
            </button>
            {images.length > 1 && (
              <button
                type="button"
                className="mg-back-btn mg-back-btn--sm"
                onClick={closeLightbox}
              >
                All photos
              </button>
            )}
          </div>
        </div>

        {/* Stage */}
        <div className="mg-lightbox__stage" onClick={() => setZoomed((z) => !z)}>
          <img
            src={current.url}
            alt={`Product view ${activeIdx + 1}`}
            className={zoomed ? "zoomed" : ""}
            style={{ opacity: transitioning ? 0 : 1 }}
            draggable={false}
          />

          <button
            className="mg-nav-btn mg-nav-btn--prev"
            onClick={(e) => { e.stopPropagation(); goTo(activeIdx - 1); }}
            disabled={activeIdx === 0}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6"/>
            </svg>
          </button>
          <button
            className="mg-nav-btn mg-nav-btn--next"
            onClick={(e) => { e.stopPropagation(); goTo(activeIdx + 1); }}
            disabled={activeIdx === images.length - 1}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 18 15 12 9 6"/>
            </svg>
          </button>

          <div className="mg-counter-pill">{activeIdx + 1} / {images.length}</div>
        </div>
        <div className="mg-lightbox__strip" ref={stripRef}>
          {images.map((img, i) => (
            <div
              key={img.id}
              className={`mg-strip-thumb${i === activeIdx ? " active" : ""}`}
              onClick={() => goTo(i)}
            >
              <img src={img.url} alt={`thumb-${i + 1}`} loading="lazy" />
            </div>
          ))}
        </div>
      </div>
    );
  };

  // ── MASONRY GRID ──────────────────────────────────────
  const renderGrid = () => (
    <div className="mg-body">
      {/* Left Rail */}
      <div className="mg-rail" ref={railRef}>
        {images.map((img, i) => (
          <div
            key={img.id}
            className={`mg-rail-thumb${i === activeIdx ? " active" : ""}`}
            onClick={() => openLightbox(img.id)}
          >
            <img src={img.url} alt={`rail-${i + 1}`} loading="lazy" />
            <span className="mg-rail-thumb__num">{i + 1}</span>
          </div>
        ))}
      </div>

      {/* Masonry Grid */}
      <div className="mg-grid-wrap">
        <div className="mg-grid">
          {images.map(({ id, url }, i) => (
            <div
              key={id}
              // @ts-ignore
              ref={id === Number(lastViewedPhoto) ? lastViewedPhotoRef : null}
              className={`mg-grid-item${i === activeIdx ? " active-item" : ""}`}
              onClick={() => openLightbox(id)}
            >
              <img
                src={url}
                alt={`View ${i + 1}`}
                width={720}
                height={480}
                sizes="(max-width: 640px) 100vw, (max-width: 1280px) 50vw, 350px"
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // ── MAIN RENDER ───────────────────────────────────────
  return (
    <>
      <Transition appear show={isShowModal} as={Fragment}>
        <Dialog as="div" className="relative z-[100]" onClose={closeGallery}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-[#0A1120]" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-hidden">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-5"
              enterTo="opacity-100 translate-y-0"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-5"
            >
              <Dialog.Panel className="w-full h-full">
                <div className="mg-wrap h-full flex flex-col">

                  {/* TOP BAR */}
                  <div className="mg-bar">
                    <div className="mg-bar__left">
                      <span className="mg-bar__title">Product Gallery</span>
                      <span className="mg-bar__count">{images.length} photos</span>
                    </div>
                    <button
                      type="button"
                      className="mg-close-btn"
                      onClick={closeGallery}
                      aria-label="Close gallery"
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                      Close
                    </button>
                  </div>

                  {/* GRID */}
                  {renderGrid()}

                  {/* LIGHTBOX (renders on top when a photo is clicked) */}
                  {renderLightbox()}

                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>
    </>
  );
};

export default ListingImageGallery;